// Reports + Analytics API helpers — CHG-1659.
//
// Consumes /api/v1/reports/* + /api/v1/analytics/* admin endpoints
// (require_permission alora-giving.analytics.read). Spec authority:
// specs/tier-05-experience/{analytics-dashboard,reports,annual-reports,
// impact-reports}.entity-hint.yaml. The 4 pages are documented
// hand-roll exceptions per DRIFT-702 (generator can't emit dashboard_v2
// / menu_v2 modes today).

import { apiClient } from "./client";

// ---------------------------------------------------------------------------
// /api/v1/reports/impact — org-level impact summary
// ---------------------------------------------------------------------------

export type ImpactPeriod = "7d" | "30d" | "90d" | "365d" | "ytd";

export interface ReportsImpact {
  period: string;
  total_donations: number;
  total_raised_minor_units: number;
  unique_donors: number;
  programs_funded: number;
  campaigns_goal_met: number;
  campaigns_total: number;
}

export async function fetchReportsImpact(
  period: ImpactPeriod = "365d",
): Promise<ReportsImpact> {
  const r = await apiClient().get<ReportsImpact>("/reports/impact", {
    params: { period },
  });
  return r.data;
}

// ---------------------------------------------------------------------------
// /api/v1/reports/annual/{year} — calendar-year summary
// ---------------------------------------------------------------------------

export interface AnnualReportByMonth {
  month: number;
  count: number;
  total_minor_units: number;
}

export interface AnnualReportBySource {
  source: string;
  count: number;
  total_minor_units: number;
}

export interface AnnualReportTopDonor {
  donor_id: string;
  name: string;
  donation_count: number;
  total_minor_units: number;
}

export interface AnnualReport {
  year: number;
  total_donations: number;
  total_raised_minor_units: number;
  unique_donors: number;
  by_month: AnnualReportByMonth[];
  by_source: AnnualReportBySource[];
  by_payment_method: AnnualReportBySource[];
  top_donors: AnnualReportTopDonor[];
}

export async function fetchAnnualReport(year: number): Promise<AnnualReport> {
  const r = await apiClient().get<AnnualReport>(`/reports/annual/${year}`);
  return r.data;
}

// ---------------------------------------------------------------------------
// /api/v1/analytics/donations/timeline — admin analytics KPI source
// ---------------------------------------------------------------------------

export type AnalyticsPeriod = "7d" | "30d" | "90d" | "365d" | "ytd";

export interface AnalyticsTimelineBucket {
  date: string;
  count: number;
  total_minor_units: number;
}

export interface AnalyticsTimeline {
  period: string;
  buckets: AnalyticsTimelineBucket[];
  /** Sum across buckets — convenience for the AnalyticsDashboard StatStrip. */
  total_count?: number;
  total_minor_units?: number;
}

export async function fetchAnalyticsTimeline(
  period: AnalyticsPeriod = "30d",
): Promise<AnalyticsTimeline> {
  const r = await apiClient().get<AnalyticsTimeline>(
    "/analytics/donations/timeline",
    { params: { period } },
  );
  // Synthesize totals when the endpoint omits them (BE typically returns
  // buckets only; the StatStrip needs scalar totals).
  const data = r.data;
  if (data.total_count == null || data.total_minor_units == null) {
    let count = 0;
    let cents = 0;
    for (const b of data.buckets ?? []) {
      count += b.count;
      cents += b.total_minor_units;
    }
    return { ...data, total_count: count, total_minor_units: cents };
  }
  return data;
}

export const reportsApi = {
  fetchReportsImpact,
  fetchAnnualReport,
  fetchAnalyticsTimeline,
};
